<template>
  <div class="upload-video-component">
    <!-- Upload de fichier vidéo -->
    <el-upload
        v-if="!hasVideo && !isUploading"
        action=""
        :auto-upload="false"
        :on-change="handleVideoChange"
        :on-remove="handleVideoRemove"
        :file-list="fileList"
        accept="video/*"
        :limit="1"
        list-type="text">
      <div style="text-align: start;">
        <el-button class="btn-upload-video" type="primary" :disabled="isUploading">
          {{ isUploading ? uploadingText : uploadButtonText }}
        </el-button>
      </div>
      <div slot="tip" class="el-upload__tip">
        {{ acceptedFormatsText }}
      </div>
    </el-upload>

    <!-- Carte de progression d'upload -->
    <div v-if="isUploading" class="upload-progress">
      <p class="upload-status"></p>
      <!-- Version avec Animation de Chargement -->
      <div class="video-card-loading">
        <div class="card-header">
          <h3 class="card-title">{{ $t('socialMedia.newVideo')}}</h3>
          <div class="loading-badge">{{ $t('socialMedia.starting')}}</div>
        </div>

        <div class="video-info-container">
          <div class="video-icon">
            <i class="el-icon-video-camera"></i>
          </div>

          <div class="video-details">
            <h4 class="video-filename">{{ $t('socialMedia.uploadStarting')}}</h4>
            <p class="video-meta">{{ $t('socialMedia.treatmentVideo')}}</p>
            <div>
              <el-progress :percentage="uploadProgress" :show-text="true"></el-progress>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Affichage d'erreur d'upload -->
    <div v-if="uploadError" class="upload-error">
      <el-alert
          :title="uploadError"
          type="error"
          :closable="true"
          @close="clearUploadError">
      </el-alert>
    </div>

    <!-- Indicateur de chargement pour les vidéos -->
    <div v-if="loadingVideo" class="video-loading">
      <i class="el-icon-loading"></i>
      <p>{{ $t('socialMedia.loadingInfo')}}</p>
    </div>

    <!-- NOUVEAU: Affichage de la vidéo uploadée avec le style carte identique au screenshot -->
    <div v-if="hasVideo && !isUploading && !loadingVideo" class="uploaded-video">
      <!-- Version Compacte -->
      <div class="video-card-complete">
        <div class="card-header">
          <h3 class="card-title">{{ getFormattedDate() }}</h3>
          <div class="success-badge">
            <i class="el-icon-circle-check"></i>
          </div>
        </div>

        <div class="video-info-container">
          <div class="video-icon">
            <i class="el-icon-video-camera"></i>
          </div>

          <div class="video-details">
            <h4 class="video-filename">{{ getVideoFileName() }}</h4>

            <div class="video-stats">
<!--              <div class="stat-item">-->
<!--                <span class="stat-label">Format</span>-->
<!--                <span class="stat-value">{{ getFileExtension().toUpperCase() }}</span>-->
<!--              </div>-->
              <div class="stat-item">
                <span class="stat-label">Taille</span>
                <span class="stat-value">{{ getFormattedFileSize() }}</span>
              </div>
            </div>

            <div class="action-buttons">
              <el-button v-if="currentVideo && !currentVideo.url.startsWith('/home/web/shared/temp/repository/')" size="mini" type="primary" class="action-btn" @click="playVideo">Lire</el-button>
              <el-button
                v-if="!noneDisplayButtonRemove"
                size="mini"
                type="danger"
                class="action-btn"
                @click.stop="confirmDeleteVideo">
                Supprimer
              </el-button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { uploadVideoMixin } from '@/mixins/uploadVideoMixin'
import { HTTP_HYPER_ESPACE_BACKEND } from '@/services/http-common'
import { Upload, Alert } from 'element-ui'
import { $t } from '@/i18n'

export default {
  name: 'UploadVideo',
  components: {
    'el-upload': Upload,
    'el-alert': Alert
  },
  mixins: [uploadVideoMixin],
  props: {
    // Textes personnalisables
    uploadButtonText: {
      type: String,
      default: $t('socialMedia.choiceVideo')
    },
    uploadingText: {
      type: String,
      default: $t('socialMedia.uploadStarting')
    },
    acceptedFormatsText: {
      type: String,
      default: $t('socialMedia.acceptedFormats')
    },

    // Configuration fonctionnelle
    postId: {
      type: [String, Number],
      default: null
    },

    // Options de validation
    maxFileSize: {
      type: Number,
      default: 15 * 1024 * 1024 * 1024 // 15 GB
    },
    allowedVideoTypes: {
      type: Array,
      default: () => ['video/mp4', 'video/avi', 'video/mov', 'video/wmv', 'video/flv', 'video/webm']
    },

    // Affichage des informations
    showVideoInfo: {
      type: Boolean,
      default: true
    },
    showVideoUrl: {
      type: Boolean,
      default: false
    },

    // Nom du réseau social pour l'upload
    networkName: {
      type: String,
      default: null
    },

    // Activation des événements pour communication avec le parent (optionnel)
    enableEvents: {
      type: Boolean,
      default: false
    },
    noneDisplayButtonRemove: {
      type: Boolean,
      default: false,
      required: false
    }
  },
  data () {
    return {
      fileList: [],
      currentVideo: null,
      uploadError: null,
      loadingVideo: false,
      currentPostId: null,
      loadVideoCallCount: 0, // Protection contre les boucles infinies
      lastLoadVideoCall: 0 // Timestamp du dernier appel
    }
  },
  computed: {
    hasVideo () {
      return this.currentVideo && this.currentVideo.url
    },
    effectivePostId () {
      return this.postId || this.$route.params.id
    },
    networkParam () {
      return this.networkName || 'initial'
    },
    // Computed property pour détecter le contexte éditorial
    isEditorialContext () {
      // Détecter si on est dans le contexte des posts éditoriaux
      return this.$route.name && (
        this.$route.name.includes('socialPosts') ||
        this.$route.name.includes('editorial') ||
        this.$store.getters['socialPostsCreationPostEditorial/getEdit'] ||
        this.$store.getters['socialPostsCreationPostEditorial/getCreate']
      )
    },
    // Add computed property for the current post
    currentPostFromStore () {
      if (this.isEditorialContext) {
        return this.$store.getters['socialPostsCreationPostEditorial/getCurrentPost']
      } else {
        return this.$store.getters['postManager/getCurrentPost']
      }
    },

    // Computed property pour détecter les changements de hasVideoUpload dans le contexte éditorial
    editorialVideoUploadStatus () {
      return this.isEditorialContext ? this.$store.getters['socialPostsCreationPostEditorial/getStatusMediaPost'].hasVideoUpload : false
    },

    // Computed property pour détecter les changements de hasVideoUpload dans le contexte postManager
    postManagerVideoUploadStatus () {
      return !this.isEditorialContext ? this.$store.getters['postManager/getStatusMediaPost'].hasVideoUpload : false
    }
  },
  watch: {
    // Watcher simplifié pour l'ID du post uniquement - SANS analyse automatique
    effectivePostId: {
      immediate: true,
      handler (newId, oldId) {
        if (newId && newId !== oldId) {
          this.currentPostId = newId
          // Charger les données vidéo sans déclencher d'analyse
          this.loadVideoForPost()
        }
      }
    },

    // Watcher pour détecter les changements de hasVideoUpload dans le contexte éditorial
    editorialVideoUploadStatus: {
      handler (newValue, oldValue) {
        if (this.isEditorialContext && newValue && !oldValue) {
          setTimeout(() => {
            this.loadVideoForPost()
          }, 100)
        }
      }
    },

    // Watcher pour détecter les changements de hasVideoUpload dans le contexte postManager
    postManagerVideoUploadStatus: {
      handler (newValue, oldValue) {
        if (!this.isEditorialContext && newValue && !oldValue) {
          setTimeout(() => {
            this.loadVideoForPost()
          }, 100)
        }
      }
    }
  },
  methods: {

    // NOUVELLES METHODES POUR LES ENCARTS
    getFileExtension () {
      if (this.currentVideo && this.currentVideo.originalData) {
        return this.currentVideo.originalData.extension || 'video'
      }
      return 'video'
    },

    // Fonction pour formater une date en français avec gestion des cas vides
    getFormattedDate () {
      const dateISO = this.currentVideo.originalData.createdAt
      // Vérifier si la date existe et n'est pas vide
      if (!dateISO || dateISO.trim() === '') {
        return ''
      }

      // Créer un objet Date
      const date = new Date(dateISO)

      // Vérifier si la date est valide
      if (isNaN(date.getTime())) {
        return 'Date invalide'
      }

      const formatter = new Intl.DateTimeFormat('fr-FR', {
        dateStyle: 'full',
        timeStyle: 'short'
      })
      return formatter.format(date)
    },

    formatFileSize (bytes) {
      if (!bytes) return 'N/A'
      const sizes = ['octets', 'Ko', 'Mo', 'Go']
      const i = Math.floor(Math.log(bytes) / Math.log(1024))
      return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i]
    },

    getFormattedFileSize () {
      if (this.currentVideo && this.currentVideo.size) {
        return this.formatFileSize(this.currentVideo.size)
      }
      if (this.currentVideo && this.currentVideo.originalData && this.currentVideo.originalData.size_formatted) {
        return this.formatFileSize(this.currentVideo.originalData.size_formatted)
      }
      return '' // Valeur par défaut comme dans le screenshot
    },

    handleVideoChange (file) {
      // Validation du fichier
      const validation = this.validateVideoFile(file.raw, {
        maxSize: this.maxFileSize,
        allowedTypes: this.allowedVideoTypes
      })

      if (!validation.valid) {
        this.uploadError = validation.error
        this.$message.error(validation.error)
        if (this.enableEvents) {
          this.$emit('upload-error', validation.error)
        }
        return
      }

      // Reset des erreurs précédentes
      this.clearUploadError()
      this.currentVideo = null

      // Commencer l'upload
      this.uploadVideo([file.raw], {
        onProgress: (progress, chunkIndex, totalChunks) => {
          if (this.enableEvents) {
            this.$emit('upload-progress', { progress, chunkIndex, totalChunks })
          }
        },
        onChunkUploaded: (chunkIndex, totalChunks, response) => {
          if (this.enableEvents) {
            this.$emit('chunk-uploaded', { chunkIndex, totalChunks, response })
          }
        }
      }).then(result => {
        if (result.success) {
          if (this.isEditorialContext) {
            // Contexte éditorial
            this.$store.dispatch('socialPostsCreationPostEditorial/invalidateTemporaryVideosCache')

            // Nettoyer l'URL vidéo pour que seul le mode upload soit actif
            this.$store.commit('socialPostsCreationPostEditorial/updateInputVideoInitial', '')

            // Attendre un peu puis déclencher une nouvelle analyse complète
            setTimeout(async () => {
              // Pour les posts éditoriaux, analyser même sans currentPostId (nouveaux posts)
              await this.$store.dispatch('socialPostsCreationPostEditorial/analyzePostMedias', this.currentPostId || null)
              await this.loadVideoForPost()
            }, 500)
          } else {
            // Contexte PostManager
            this.$store.dispatch('postManager/invalidateTemporaryVideosCache')

            // Nettoyer l'URL vidéo pour que seul le mode upload soit actif
            this.$store.commit('postManager/updatePostVideoFileUrlCustom', {
              network: 'initial',
              url: ''
            })

            // Attendre un peu puis déclencher une nouvelle analyse complète
            setTimeout(async () => {
              if (this.currentPostId) {
                await this.$store.dispatch('postManager/analyzePostMedias', this.currentPostId)
              }
              await this.loadVideoForPost()
            }, 500)
          }

          this.$message.success($t('socialMedia.uploadSuccess'))
          if (this.enableEvents) {
            this.$emit('upload-success', result)
          }
        } else {
          this.uploadError = result.error || $t('socialMedia.uploadError')
          this.$message.error(this.uploadError)
          if (this.enableEvents) {
            this.$emit('upload-error', this.uploadError)
          }
        }
      }).catch(error => {
        this.uploadError = error.message || $t('socialMedia.uploadError')
        this.$message.error(this.uploadError)
        if (this.enableEvents) {
          this.$emit('upload-error', this.uploadError)
        }
      })
    },

    handleVideoRemove (file, fileList) {
      // Utiliser la nouvelle méthode de suppression si on a une vidéo
      if (this.currentVideo && this.currentVideo.originalData && this.currentVideo.originalData.path) {
        this.deleteVideoFromServer()
      } else {
        console.error($t('socialMedia.noAllConditionToDelete'), {
          currentPostId: this.currentPostId,
          hasVideo: this.hasVideo,
          currentVideo: this.currentVideo
        })
        this.$message.error($t('socialMedia.uploadErrorData'))
      }
    },

    getVideoFileName () {
      return (this.currentVideo && this.currentVideo.name) || 'Ma vidéo téléchargée'
    },

    onVideoLoaded (event) {
      if (this.enableEvents) {
        this.$emit('video-loaded', event.target)
      }
    },

    onVideoError (event) {
      console.error('Erreur lors du chargement de la vidéo:', event)
      this.$message.warning('Impossible de charger la vignette vidéo')
      if (this.enableEvents) {
        this.$emit('video-error', event)
      }
    },

    playVideo () {
      if (this.hasVideo && this.currentVideo && this.currentVideo.url) {
        // Ouvrir la vidéo dans un nouvel onglet
        window.open(this.currentVideo.url, '_blank')

        if (this.enableEvents) {
          this.$emit('video-play-requested', this.currentVideo.url)
        }
      }
    },

    clearUploadError () {
      this.uploadError = null
    },

    clearUploadData () {
      this.currentVideo = null
      this.uploadError = null
      this.uploadProgress = 0
      this.fileList = []
    },

    // Méthodes publiques pour contrà´ler le composant
    reset () {
      this.clearUploadData()
    },

    confirmDeleteVideo () {
      this.$confirm($t('socialMedia.confirmDeleted'), $t('socialMedia.confirmDeletedTitle'), {
        confirmButtonText: $t('socialMedia.confirmDeletedButton'),
        cancelButtonText: $t('socialMedia.confirmDeletedCancelButton'),
        type: 'warning'
      }).then(() => {
        this.deleteVideoFromServer()
      }).catch(() => {
        console.log('Suppression annulée par l\'utilisateur')
      })
    },

    deleteVideoFromServer () {
      if (!this.currentVideo) {
        console.error($t('socialMedia.noVideoToDelete'))
        this.$message.error($t('socialMedia.noVideoToDelete'))
        return
      }

      // Déterminer le type de vidéo à supprimer
      const currentPost = this.currentPostFromStore
      const hasVideoInPostAssets = currentPost &&
                                   currentPost.assets &&
                                   currentPost.assets.VIDEO &&
                                   Object.keys(currentPost.assets.VIDEO).length > 0

      if (this.isEditorialContext) {
        // Contexte éditorial
        const isEditorialMode = this.$store.getters['socialPostsCreationPostEditorial/getEdit'] &&
            this.$store.getters['socialPostsCreationPostEditorial/getId']

        if (isEditorialMode && hasVideoInPostAssets) {
          // Vidéo asset du post éditorial : utiliser validatePost avec videoDeleted: true
          this.handleEditorialVideoDelete()
        } else {
          // Vidéo temporaire : utiliser l'API de suppression directe
          this.handleDirectVideoDelete()
        }
      } else {
        // Contexte postManager
        if (hasVideoInPostAssets && this.currentPostId) {
          // Vidéo asset du post : utiliser l'API pour supprimer l'asset du post
          this.handlePostManagerVideoDelete()
        } else {
          // Vidéo temporaire : utiliser l'API de suppression directe
          this.handleDirectVideoDelete()
        }
      }
    },

    handleEditorialVideoDelete () {
      // Supprimer de this.fileList
      this.fileList = []

      // Reset des données locales
      this.clearUploadData()

      // Mettre à jour le store pour supprimer la référence vidéo
      this.$store.commit('socialPostsCreationPostEditorial/setStatusMediasPost', { key: 'hasVideoUpload', value: false })
      this.$store.commit('socialPostsCreationPostEditorial/clearUploadedVideoPath')

      // Utiliser validatePost pour sauvegarder les modifications
      this.$store.dispatch('socialPostsCreationPostEditorial/validatePost', {
        typePost: 'B', // Brouillon
        videoDeleted: true
      }).then(() => {
        // Mettre à jour l'état des boutons après la suppression de la vidéo
        this.$store.commit('socialPostsCreationPostEditorial/checkPublishButtonState')
        this.$store.commit('socialPostsCreationPostEditorial/checkDraftButtonState')

        // Émettre les événements
        this.$emit('video-removed', { network: this.networkParam })
        if (this.enableEvents) {
          this.$emit('video-removed')
        }

        this.$message.success($t('socialMedia.videoDeleted'))
      }).catch(error => {
        this.$message.error($t('socialMedia.videoDeletedError'))
        if (this.enableEvents) {
          this.$emit('delete-error', error)
        }
      })
    },

    handlePostManagerVideoDelete () {
      // Supprimer l'asset vidéo du post via l'API
      if (!this.currentVideo.originalData || !this.currentVideo.originalData.id) {
        this.$message.error($t('socialMedia.uploadErrorData') + ' - id manquant')
        return
      }

      const assetId = this.currentVideo.originalData.id
      const postId = this.currentPostId

      HTTP_HYPER_ESPACE_BACKEND.delete(`/annonce/${postId}/video/${assetId}`).then(response => {
        // Supprimer de this.fileList
        this.fileList = []

        // Reset des données locales
        this.clearUploadData()

        // Mettre à jour le store
        this.$store.commit('postManager/setStatusMediasPost', { key: 'hasVideoUpload', value: false })
        this.$store.commit('postManager/clearUploadedVideoPath')

        // Invalider le cache et recharger le post pour mettre à jour les assets
        this.$store.dispatch('postManager/invalidateTemporaryVideosCache')
        this.$store.dispatch('postManager/loadPost', postId).then(() => {
          // Analyser les médias après rechargement
          setTimeout(() => {
            this.$store.dispatch('postManager/analyzePostMedias', postId)
          }, 100)
        })

        // Émettre les événements
        this.$emit('video-removed', { network: this.networkParam })
        if (this.enableEvents) {
          this.$emit('video-removed')
        }

        this.$message.success($t('socialMedia.videoDeleted'))
      }).catch(error => {
        const errorMessage = error.response && error.response.data && error.response.data.error
          ? error.response.data.error
          : error.message || 'Erreur inconnue'
        this.$message.error($t('socialMedia.videoDeletedError') + `: ${errorMessage}`)
        if (this.enableEvents) {
          this.$emit('delete-error', error)
        }
      })
    },

    handleDirectVideoDelete () {
      if (this.currentVideo.originalData && this.currentVideo.originalData.path) {
        HTTP_HYPER_ESPACE_BACKEND.delete('/upload/videos/delete', {
          data: { path: this.currentVideo.originalData.path }
        }).then(response => {
          // Supprimer de this.fileList
          this.fileList = []

          // Reset des données locales
          this.clearUploadData()

          if (this.isEditorialContext) {
            // Mettre à jour le store
            this.$store.commit('socialPostsCreationPostEditorial/setStatusMediasPost', { key: 'hasVideoUpload', value: false })
            this.$store.commit('socialPostsCreationPostEditorial/clearUploadedVideoPath')

            // S'assurer qu'il n'y a pas de redirection lors de la suppression de vidéo
            this.$store.commit('socialPostsCreationPostEditorial/setRedirection', false)

            // Mettre à jour l'état des boutons après la suppression de la vidéo
            this.$store.commit('socialPostsCreationPostEditorial/checkPublishButtonState')
            this.$store.commit('socialPostsCreationPostEditorial/checkDraftButtonState')
          } else {
            this.$store.commit('postManager/setStatusMediasPost', { key: 'hasVideoUpload', value: false })
            this.$store.commit('postManager/clearUploadedVideoPath')
          }

          // Invalider le cache et nettoyer les données locales après suppression
          if (this.isEditorialContext) {
            this.$store.dispatch('socialPostsCreationPostEditorial/invalidateTemporaryVideosCache')
          } else {
            this.$store.dispatch('postManager/invalidateTemporaryVideosCache')
          }
          this.clearUploadData()

          // Émettre les événements
          this.$emit('video-removed', { network: this.networkParam })
          if (this.enableEvents) {
            this.$emit('video-removed')
          }

          this.$message.success($t('socialMedia.videoDeleted'))
        }).catch(error => {
          console.error($t('socialmedia.videoDeletedError'), error)
          const errorMessage = error.response && error.response.data && error.response.data.error
            ? error.response.data.error
            : error.message || 'Erreur inconnue'
          this.$message.error($t('socialmedia.videoDeletedError') + ` ${errorMessage}`)
          if (this.enableEvents) {
            this.$emit('delete-error', error)
          }
        })
      } else {
        this.$message.error($t('socialmedia.uploadErrorData') + '- chemin manquant')
      }
    },

    loadVideoForPost () {
      // Protection contre les boucles infinies
      const now = Date.now()
      if (now - this.lastLoadVideoCall < 1000) { // Moins d'1 seconde depuis le dernier appel
        this.loadVideoCallCount++
        if (this.loadVideoCallCount > 5) {
          this.loadingVideo = false
          return Promise.resolve()
        }
      } else {
        this.loadVideoCallCount = 0 // Reset si plus d'1 seconde s'est écoulée
      }
      this.lastLoadVideoCall = now

      this.loadingVideo = true

      // Utiliser les données déjà analysées par le store
      const currentPost = this.currentPostFromStore
      const statusMedias = this.isEditorialContext
        ? this.$store.getters['socialPostsCreationPostEditorial/getStatusMediaPost']
        : this.$store.getters['postManager/getStatusMediaPost']

      // console.log('Status medias : ', statusMedias)
      // Dans le contexte éditorial, currentPost peut être null pour les nouveaux posts
      if (!currentPost && !this.isEditorialContext) {
        this.loadingVideo = false
        return Promise.resolve()
      }

      // 1. Vérifier les vidéos dans le post (priorité 1) - seulement si on a un post
      if (currentPost && statusMedias.hasVideoUpload && currentPost.assets && currentPost.assets.VIDEO) {
        const videoAssets = currentPost.assets.VIDEO
        const fluxDest = ''

        if (this.isEditorialContext) {
          // Contexte éditorial : chercher les vidéos dans SAISIE_EDITO
          if (videoAssets[fluxDest] && videoAssets[fluxDest].medias && videoAssets[fluxDest].medias.length > 0) {
            const videoMedia = videoAssets[fluxDest].medias[0]

            if (videoMedia.productionFile && videoMedia.productionFile.url) {
              this.setCurrentVideoFromPost(videoMedia)
              this.loadingVideo = false
              return Promise.resolve()
            }
          }
        } else {
          // Contexte postManager : chercher dans tous les types d'assets vidéo
          const videoTypes = Object.keys(videoAssets)
          for (const videoType of videoTypes) {
            const videoTypeAssets = videoAssets[videoType]
            if (videoTypeAssets && videoTypeAssets.medias && videoTypeAssets.medias.length > 0) {
              const videoMedia = videoTypeAssets.medias[0]

              if (videoMedia.productionFile && videoMedia.productionFile.url) {
                this.setCurrentVideoFromPost(videoMedia)
                this.loadingVideo = false
                return Promise.resolve()
              }
            }
          }
        }
      }

      // 2. Vérifier les vidéos temporaires (si pas de vidéo dans le post ou pas de post)
      if (statusMedias.hasVideoUpload) {
        this.loadTemporaryVideo()
        return Promise.resolve()
      }

      // 3. Aucune vidéo trouvée
      this.currentVideo = null
      this.fileList = []
      this.loadingVideo = false
      return Promise.resolve()
    },

    setCurrentVideoFromPost (videoMedia) {
      this.currentVideo = {
        name: (videoMedia.source && videoMedia.source.srcValue || videoMedia.sourceFile) ? videoMedia.source.srcValue.split('/').pop() : videoMedia.sourceFile.name,
        originalName: videoMedia.sourceFile ? videoMedia.sourceFile.name : $t('socialMedia.videoOfPost'),
        url: videoMedia.productionFile.url,
        size: null,
        uploadDate: null,
        network: 'initial',
        id: videoMedia.id,
        originalData: {
          ...videoMedia,
          path: videoMedia.source ? videoMedia.source.srcValue : null,
          created_at: videoMedia.createdAt,
          size_formatted: videoMedia.sourceFile && videoMedia.sourceFile.properties.SIZE,
          extension: videoMedia.sourceFile && videoMedia.sourceFile.properties.VIDEO_TYPE
        }
      }

      this.fileList = [{
        name: this.currentVideo.name,
        url: this.currentVideo.url,
        status: 'success',
        uid: this.currentVideo.id || Date.now()
      }]

      if (this.enableEvents) {
        this.$emit('video-ready', this.currentVideo)
      }
    },

    async loadTemporaryVideo () {
      try {
        // Utiliser le cache du store au lieu d'un appel direct
        const videosData = this.isEditorialContext
          ? await this.$store.dispatch('socialPostsCreationPostEditorial/checkTemporaryVideos')
          : await this.$store.dispatch('postManager/checkTemporaryVideos')

        if (videosData.count > 0) {
          const video = videosData.videos[0]
          this.currentVideo = {
            name: video.filename || video.originalName || $t('socialMedia.videoUploading'),
            originalName: video.originalName,
            url: video.path,
            size: video.size,
            uploadDate: video.created_at,
            network: video.network,
            id: video.id,
            originalData: {
              ...video,
              path: video.path
            }
          }

          this.fileList = [{
            name: this.currentVideo.name,
            url: this.currentVideo.url,
            status: 'success',
            uid: this.currentVideo.id || Date.now()
          }]

          if (this.enableEvents) {
            this.$emit('video-ready', this.currentVideo)
          }
        }
      } catch (error) {
        console.error($t('socialMedia.tempError'), error)
        this.currentVideo = null
        this.fileList = []
      } finally {
        this.loadingVideo = false
      }
    }
  },

  mounted () {
    // Charger automatiquement la vidéo au montage
    this.$nextTick(() => {
      if (this.currentPostId) {
        this.loadVideoForPost()
      } else if (this.isEditorialContext) {
        // Pour les nouveaux posts éditoriaux, vérifier les vidéos temporaires
        setTimeout(() => {
          this.loadVideoForPost()
        }, 200)
      }
    })
  }
}
</script>

<style lang="scss" scoped>
.upload-video-component {
  .upload-progress {
    margin-top: 15px;
  }

  .upload-status {
    text-align: center;
    margin-top: 10px;
    font-size: 14px;
    color: #606266;
  }

  .upload-error {
    margin-top: 15px;
  }

  .uploaded-video {
    margin-top: 15px;
  }

  // NOUVEAU STYLE CARTE - Identique au screenshot
  .video-card-complete {
    background: white;
    border: 1px solid #e9ecef;
    overflow: hidden;
    max-width: 500px;
    margin-bottom: 20px;

    .card-header {
      background: #f8f9fa;
      padding: 12px 20px;
      border-bottom: 1px solid #e9ecef;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .card-title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #495057;
      }

        .success-badge {
          padding: 4px 12px;
          border-radius: 20px;
          font-size: 12px;
          font-weight: 500;
          display: flex;
          align-items: center;

          i {
            font-size: 18px;
            color: #67C23A;
          }

          .check-icon {
            margin-left: 4px;
            font-size: 14px;
            font-weight: bold;
          }
        }

      .loading-badge {
        background: #17a2b8;
        color: white;
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 500;
      }
    }

    .video-info-container {
      padding: 20px;
      display: flex;
      align-items: flex-start;
      gap: 15px;

      .video-icon {
        width: 50px;
        height: 50px;
        background: #409EFF;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        i {
          color: white;
          font-size: 24px;
        }
      }

      .video-details {
        flex: 1;

        .video-filename {
          margin: 0 0 8px 0;
          font-size: 16px;
          font-weight: 600;
          color: #343a40;
        }

        .video-meta {
          margin: 0 0 15px 0;
          font-size: 14px;
          color: #6c757d;
        }

        .video-stats {
          display: flex;
          gap: 30px;
          margin-bottom: 20px;

          .stat-item {
            display: flex;
            flex-direction: column;

            .stat-label {
              font-size: 12px;
              color: #6c757d;
              font-weight: 500;
              margin-bottom: 4px;
            }

            .stat-value {
              font-size: 14px;
              color: #343a40;
              font-weight: 600;
            }
          }
        }

        .action-buttons {
          display: flex;
          gap: 12px;
          justify-content: flex-end;

          .action-btn {
            font-size: 1rem !important;
            padding: 8px 20px !important;
            border-radius: 5px !important;
            font-weight: 500 !important;
            min-height: 34px !important;
            min-width: 80px !important;
            border: none !important;

            &.el-button--danger {
              background: #dc3545 !important;

              &:hover {
                background: #c82333 !important;
              }
            }
          }
        }
      }
    }
  }

  .video-card-loading {
    background: white;
    border: 1px solid #e9ecef;
    overflow: hidden;
    max-width: 500px;
    margin-bottom: 20px;

    .card-header {
      background: #f8f9fa;
      padding: 12px 20px;
      border-bottom: 1px solid #e9ecef;
      display: flex;
      justify-content: space-between;
      align-items: center;

      .card-title {
        margin: 0;
        font-size: 16px;
        font-weight: 600;
        color: #495057;
      }

      .loading-badge {
        background: #17a2b8;
        color: white;
        padding: 4px 12px;
        border-radius: 20px;
        font-size: 12px;
        font-weight: 500;
      }
    }

    .video-info-container {
      padding: 20px;
      display: flex;
      align-items: flex-start;
      gap: 15px;

      .video-icon {
        width: 50px;
        height: 50px;
        background: #409EFF;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        flex-shrink: 0;

        i {
          color: white;
          font-size: 24px;
        }
      }

      .video-details {
        flex: 1;

        .video-filename {
          margin: 0 0 8px 0;
          font-size: 16px;
          font-weight: 600;
          color: #343a40;
        }

        .video-meta {
          margin: 0 0 15px 0;
          font-size: 14px;
          color: #6c757d;
        }

        .video-stats {
          display: flex;
          gap: 30px;

          .stat-item {
            display: flex;
            flex-direction: column;

            .stat-label {
              font-size: 12px;
              color: #6c757d;
              font-weight: 500;
              margin-bottom: 4px;
            }

            .stat-value {
              font-size: 14px;
              color: #343a40;
              font-weight: 600;
            }
          }
        }
      }
    }
  }

  // Badge de statut pour la version simplifiée - Style professionnel
  .status-badge-simple {
    position: absolute;
    top: 15px;
    right: 15px;
    background: #28a745;
    color: white;
    padding: 4px 10px;
    font-size: 11px;
    font-weight: 500;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    z-index: 10;

    .check-icon {
      margin-left: 4px;
      vertical-align: middle;
      font-size: 11px;
      font-weight: bold;
    }
  }

  .video-thumbnail-container {
    position: relative;
    height: 200px;
    background-color: #000;
    overflow: hidden;
    cursor: pointer;
  }

  .video-thumbnail {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.3s ease;

    &:hover {
      opacity: 0.8;
    }
  }

  .play-overlay {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    background: rgba(0,0,0,0.6);
    border-radius: 50%;
    width: 60px;
    height: 60px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 24px;
    cursor: pointer;
    transition: background 0.3s ease;

    &:hover {
      background: rgba(0,0,0,0.8);
    }
  }

  .status-badge {
    position: absolute;
    top: 15px;
    right: 15px;
    background: #28a745;
    color: white;
    padding: 8px 16px; // Plus d'espace pour un badge plus équilibré
    border-radius: 20px; // Plus arrondi pour ressembler à la deuxième image
    font-size: 12px;
    font-weight: 600;
    z-index: 2;
    display: flex;
    align-items: center;
    gap: 6px; // Plus d'espace entre le texte et l'icône
    white-space: nowrap;
    min-width: fit-content; // S'adapte au contenu
    height: auto; // Hauteur automatique
    line-height: 1; // Ligne de base pour éviter les déformations

    .check-icon {
      font-size: 12px;
      animation: checkBounce 0.6s ease-in-out;
      display: inline-flex; // Meilleur alignement de l'icône
      align-items: center;
    }
  }

  .video-info-section {
    padding: 15px;
    background: white;
  }

  .video-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
  }

  .video-title {
    font-size: 14px;
    font-weight: 600;
    color: #2c3e50;
    margin: 0;
  }

  .delete-btn {
    background: #dc3545;
    color: white;
    border: none;
    border-radius: 4px;
    padding: 6px 12px;
    font-size: 12px;
    cursor: pointer;
    font-weight: 500;
    transition: background-color 0.2s ease;

    &:hover {
      background: #c82333;
    }
  }

  .video-metadata {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0 20px;
    line-height: 1.6;
  }

  .metadata-row {
    display: contents;
  }

  .metadata-label {
    font-size: 13px;
    color: #6c757d;
    font-weight: 500;
    padding: 4px 0;
  }

  .metadata-value {
    font-size: 13px;
    color: #343a40;
    font-weight: 600;
    padding: 4px 0;
    text-align: right;
  }

  .video-url {
    margin: 16px 0 0 0;
    padding: 8px;
    background-color: #f5f7fa;
    font-size: 11px;
    color: #606266;
    word-break: break-all;
    font-family: monospace;
    border: 1px solid #e9ecef;
  }

  // ANCIEN STYLE - Conservé pour compatibilité si nécessaire
  .video-thumbnail-container-old {
    position: relative;
    margin-top: 15px;
    overflow: hidden;
    background-color: #000;
    border: 1px solid #dadee0;
  }

  .video-info {
    padding: 15px;
    background-color: white;
    border-top: 1px solid #e9ecef;
  }

  .video-name {
    margin: 0 0 8px 0;
    font-weight: 600;
    color: #303133;
    font-size: 14px;
  }

  .video-name-container {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 12px;
    border-bottom: 1px solid #f1f3f4;
  }

  .delete-video-btn {
    margin-left: 10px;
    flex-shrink: 0;
  }

  // ANCIENS ENCARTS - Conservés pour compatibilité
  .video-meta-cards {
    display: flex;
    gap: 20px;
    margin-bottom: 16px;
    justify-content: space-between;
    align-items: flex-start;
  }

  .meta-card {
    flex: 1;
    background: #f8f9fa;
    padding: 10px 12px;
    border: 1px solid #e9ecef;
    text-align: center;
    transition: background-color 0.2s ease;

    &:hover {
      background: #f1f3f4;
    }
  }

  .meta-label {
    font-size: 11px;
    color: #6c757d;
    font-weight: 500;
    margin-bottom: 4px;
    text-transform: uppercase;
    letter-spacing: 0.3px;
  }

  .meta-value {
    font-size: 13px;
    color: #343a40;
    font-weight: 600;
    line-height: 1.2;
  }

  .extension-badge {
    display: inline-block;
    background: #28a745;
    color: white;
    padding: 1px 5px;
    font-size: 8px;
    font-weight: 600;
    text-transform: uppercase;
    margin-left: 3px;
    vertical-align: middle;
  }

  .btn-upload-video {
    font-size: 1rem !important;
    padding: 7px 15px !important;
    border-radius: 4px !important;
    font-weight: 500 !important;
    line-height: 1 !important;
    min-height: 28px !important;
  }

  .video-uploaded-status {
    color: #303133;
    font-weight: 500;
    display: flex;
    align-items: center;
    margin-top: 10px;

    .el-icon-check {
      margin-left: 8px;
      color: #67C23A !important;
      font-size: 16px;
    }

    .animated-check {
      animation: checkBounce 0.6s ease-in-out;
    }
  }

  .video-loading {
    text-align: center;
    padding: 20px;
    color: #606266;
    font-size: 14px;

    .el-icon-loading {
      font-size: 18px;
      margin-right: 8px;
    }
  }

  @keyframes checkBounce {
    0% {
      transform: scale(0);
      opacity: 0;
    }
    50% {
      transform: scale(1.2);
      opacity: 1;
    }
    100% {
      transform: scale(1);
      opacity: 1;
    }
  }
}
</style>
